import type {
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
} from "lexical";

import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import * as React from "react";
import { DOMExportOutput } from "lexical";
import { InPostSearchForm } from "../../../features/post/components/InPostSearchForm";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { SearchFormMock } from "./SearchFormMock";

type SearchFormComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  searchFormClassName?: string;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
}>;

function SearchFormComponent({
  className,
  searchFormClassName,
  format,
  nodeKey,
}: SearchFormComponentProps) {
  const [editor] = useLexicalComposerContext();
  return (
    <BlockWithAlignableContents
      className={className}
      format={format}
      nodeKey={nodeKey}
    >
      <div className={searchFormClassName}>
        {!editor.isEditable() ? <InPostSearchForm /> : <SearchFormMock />}
      </div>
    </BlockWithAlignableContents>
  );
}

export type SerializedSearchFormNode = SerializedDecoratorBlockNode;

export class SearchFormNode extends DecoratorBlockNode {
  static getType(): string {
    return "searchForm";
  }

  static clone(node: SearchFormNode): SearchFormNode {
    return new SearchFormNode(node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedSearchFormNode): SearchFormNode {
    const node = $createSearchFormNode();
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedSearchFormNode {
    return {
      ...super.exportJSON(),
      type: "searchForm",
      version: 1,
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("div");
    const mock = document.createElement("div");
    const inner = document.createElement("div");
    const categoryTab = document.createElement("div");
    const tab = document.createElement("div");
    const formBlock = document.createElement("div");
    const theme = editor._config.theme;
    const className = theme.searchForm;
    if (className !== undefined) {
      element.className = className;
    }

    mock.className = "mock";
    inner.className = "inner";
    categoryTab.className = "category-tab";
    tab.className = "tab";
    formBlock.className = "form-block";

    inner.appendChild(categoryTab);
    categoryTab.appendChild(tab);
    inner.appendChild(formBlock);
    mock.appendChild(inner);
    element.appendChild(mock);

    return { element };
  }

  constructor(format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
  }

  updateDOM(): false {
    return false;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || "",
      focus: embedBlockTheme.focus || "",
    };
    const searchFormClassName = config.theme.searchForm;
    return (
      <SearchFormComponent
        className={className}
        searchFormClassName={searchFormClassName}
        format={this.__format}
        nodeKey={this.getKey()}
      />
    );
  }
}

export function $createSearchFormNode(): SearchFormNode {
  return new SearchFormNode();
}

export function $isSearchFormNode(
  node: SearchFormNode | LexicalNode | null | undefined
): node is SearchFormNode {
  return node instanceof SearchFormNode;
}
